import type { BlockerType, Issue, ProjectMeta } from './types';
import { BLOCKER_ACTION, BLOCKER_LABEL, BLOCKER_TYPES, STATUS_LABELS } from './types';
import { outstandingChecks } from './done';
import { blockerOf, stillBlocked } from './blocked';

/**
 * What has to happen for a blocked item to move.
 *
 * The kind of blocker is read from the item's own `blockerType` field. Where
 * that is missing — anything written before the field existed — it is guessed
 * from the wording instead, so an older board keeps working untouched.
 *
 * Guessing is a fallback, never the design. It was measured against ordinary
 * human phrasing and understood none of it: "phone is dead lol" and "Need to
 * see the numbers first" both fell through. It only ever worked because one
 * author wrote every entry in one house style.
 */
export interface UnblockAction {
  key: string;
  type: BlockerType;
  label: string; // the thing to actually do
  detail: string; // why it is phrased that way
  /**
   * True when every item in the group waits on the identical thing, so clearing
   * it releases all of them. False for a category — six items waiting on "a
   * measurement" may be waiting on six different measurements, and saying one
   * action frees all six would be a lie.
   */
  exact: boolean;
  issues: Issue[];
  /**
   * The item doing the blocking, when the blocker IS an item on this board.
   *
   * Only a dependency has one. "Waiting on a measurement" is a kind of wait,
   * not a card — there is nothing to open and nobody to assign it to. So this
   * is undefined for every other kind, and the card drops the buttons that
   * would need it rather than pointing them at the blocked items instead.
   */
  blocker?: Issue;
}

/**
 * Best guess at the kind of blocker, from words any project might use.
 *
 * This is a fallback for items written before the field existed, and nothing
 * more. It is deliberately weak: measured against ordinary phrasing it
 * understood almost none of it, and it only ever appeared to work because one
 * person wrote every entry in one house style.
 *
 * That house style used to be IN here — "Fold is down", "unplugged", "on-device",
 * "census", "floor test". One customer's phone vocabulary, shipped to everybody,
 * guessing confidently and wrongly on any board but theirs. Removed: a fallback
 * that is silent is honest, and one that is confidently wrong is not.
 *
 * What is left is wording that means the same thing on a phone app, a building
 * site or a law firm. If it recognises nothing, it says nothing, and the person
 * chooses from the list — which is the design, and always was.
 */
const PATTERNS: { type: BlockerType; test: RegExp }[] = [
  { type: 'person', test: /mock-?up|approval|approve|sign ?off/i },
  // "test" as a stem, so testing and tests count. \b on the left only, or
  // "latest" matches. Profiling is a measurement whatever is being built.
  { type: 'test', test: /\btest|measur|reading|benchmark|profil|check that|confirmation that/i },
  { type: 'design', test: /needs a design|design first|needs a plan|re-worked approach|not attempted/i },
  { type: 'decision', test: /decision|decide|your call|needs a call/i },
  { type: 'parked', test: /parked by choice|parked deliberately|pick it up when/i },
  { type: 'bundled', test: /bundled with|ride the/i },
];

/** How to word an action once the specific thing is known. */
export function namedAction(type: BlockerType, target: string): string {
  switch (type) {
    case 'person':
      return `${target} needs to act`;
    case 'decision':
      return `Decide: ${target}`;
    case 'external':
      return `Chase ${target}`;
    case 'test':
      return `Run ${target}`;
    case 'design':
      return `Design ${target}`;
    case 'bundled':
      return `Goes with ${target}`;
    case 'parked':
      return `Parked until ${target}`;
    default:
      return `Waiting on ${target}`;
  }
}

const DETAIL: Record<BlockerType, string> = {
  item: 'Nothing else is holding these back.',
  person: 'Waiting on a person before any work can start.',
  decision: 'Waiting on a decision only you can make.',
  external: 'Waiting on someone outside the team.',
  test: 'Waiting on a number or a real-world check.',
  design: 'Deliberately not built until the approach is worked out.',
  bundled: 'Riding along with other changes to the same area.',
  parked: 'Nothing is stopping these — they were set aside deliberately.',
  other: 'The reason has not been classified — open them to see.',
};

/**
 * The extra warning that only makes sense when nobody said WHICH thing.
 *
 * It used to live inside the detail above, so it was printed either way — and
 * an item waiting on a named test read "these may not be the same test" in the
 * very sentence where the test had just been named. It contradicted itself.
 */
const UNNAMED: Partial<Record<BlockerType, string>> = {
  test: 'These may not be the same test.',
};

/**
 * What clearing this group would actually achieve, in words.
 *
 * A function so the awkward cases can be tested with chosen input, rather than
 * depending on whichever groups a particular board happens to contain. "Clearing
 * it releases all 1" was on screen for weeks: true, correctly counted, and not
 * something any person would say.
 */
export function unblockEffect(exact: boolean, count: number, detail: string): string {
  if (!exact) {
    return `${count} item${count === 1 ? ' is' : 's are'} waiting on this. ${detail}`;
  }
  return count === 1
    ? `${detail} Clearing it would release it.`
    : `${detail} Clearing it releases all ${count}.`;
}

/**
 * The one action worth doing before any other, worded for the hero card.
 *
 * A dependency has a card behind it, so it is named: "Resolve <its title>
 * (#140)". Every other kind of wait has no card — "Run the cellular load test"
 * is already the whole instruction, and dressing it as "Resolve Run the
 * cellular load test" would be nonsense. So that case keeps its own wording.
 */
export function leverageTitle(action: Pick<UnblockAction, 'label' | 'blocker'>): string {
  /*
   * NO VERB IN FRONT OF THE TITLE.
   *
   * It read "Resolve <title> (#140)", which works only while no title begins
   * with a verb of its own. On a well-kept board most of them do, and the card
   * came out as "Resolve Decide how holidays are represented (#6)".
   *
   * Detecting a leading verb is not something worth attempting. The card is
   * already headed HIGHEST LEVERAGE ACTION and the buttons underneath say what
   * can be done about it, so the instruction is carried by the card and the
   * title can simply be the title.
   */
  return action.blocker ? `${action.blocker.title} (${action.blocker.ref})` : action.label;
}

/** "3 downstream issues", or one of them. */
export function downstreamCount(count: number): string {
  return `${count} downstream issue${count === 1 ? '' : 's'}`;
}

/**
 * A blocked item named in the sentence: "#142 OAuth".
 *
 * Titles are cut at this length because the hero card prints every one of them
 * on a single line. The design's own examples are two words each; a real board
 * has titles of eighty characters, and three of those would wrap the line into
 * a paragraph and bury the buttons under it.
 */
export function downstreamName(issue: Pick<Issue, 'ref' | 'title'>, max = 26): string {
  const title = issue.title.trim();
  return `${issue.ref} ${title.length > max ? `${title.slice(0, max - 1).trimEnd()}…` : title}`;
}

/**
 * WHAT THE BOARD CAN CHECK BEFORE CLEARING A BLOCKER.
 *
 * Clearing a blocker is a claim: the thing these were waiting on has happened.
 * Nothing was ever asked before, so one press said "it is done" on behalf of
 * five issues with no evidence of any kind.
 *
 * The evidence is the blocking card itself. On a board — run by a person or by
 * an assistant — finishing work means UPDATING ITS CARD: moving it to Done and
 * ticking whatever this project requires before anything may be called done. So
 * that is what is read here, rather than asking somebody to promise it.
 *
 * It reports; it does not refuse. A blocker can genuinely stop applying without
 * its card closing — the plan changed, the dependency was dropped — and a board
 * that will not let you say so is a board people work around. What it will not
 * do is let it happen silently.
 */
export type ResolveVerdict =
  /** The blocking card is closed and satisfies the project's checks. */
  | { kind: 'verified'; blocker: Issue }
  /** The blocking card is still open. */
  | { kind: 'not-done'; blocker: Issue }
  /** Closed, but the project's own checks are not all ticked. */
  | { kind: 'checks-open'; blocker: Issue; outstanding: string[] }
  /** Nobody named a card, so there is nothing to read. */
  | { kind: 'nothing-to-check' };

export function verifyResolve(
  action: Pick<UnblockAction, 'blocker'>,
  meta: ProjectMeta
): ResolveVerdict {
  const blocker = action.blocker;
  if (!blocker) return { kind: 'nothing-to-check' };
  if (!isClosed(blocker)) return { kind: 'not-done', blocker };
  const outstanding = outstandingChecks(blocker, meta);
  return outstanding.length > 0
    ? { kind: 'checks-open', blocker, outstanding }
    : { kind: 'verified', blocker };
}

/** What the board found, in words, for the person about to press the button. */
export function resolveMessage(v: ResolveVerdict): string {
  switch (v.kind) {
    case 'verified':
      return `${v.blocker.ref} is marked ${STATUS_LABELS[v.blocker.status].toLowerCase()} and every check on it is ticked.`;
    case 'not-done':
      return `${v.blocker.ref} is still ${STATUS_LABELS[v.blocker.status].toLowerCase()}. Finished work is moved to done on its own card, and this one has not been.`;
    case 'checks-open':
      return `${v.blocker.ref} is closed, but ${v.outstanding.length} check${v.outstanding.length === 1 ? '' : 's'} on it ${v.outstanding.length === 1 ? 'is' : 'are'} not ticked: ${v.outstanding.join(', ')}.`;
    case 'nothing-to-check':
      return 'Nobody has said which card these are waiting on, so there is nothing the board can check. Only you know whether it is done.';
  }
}

/**
 * Whether what the board found supports clearing it.
 *
 * Written as a type guard so the card can reach the blocking issue after
 * asking — otherwise every use needs a second check for a card that has
 * already been established to exist.
 */
export const resolveIsBacked = (
  v: ResolveVerdict
): v is Extract<ResolveVerdict, { kind: 'verified' }> => v.kind === 'verified';

/**
 * One line for everything that is stuck, split by whether it has a card.
 *
 * There were two lines saying overlapping things: how many other jobs were
 * holding work up, and separately how many items sat in the backlog. Where the
 * backlog item WAS one of the stuck ones — which it is whenever nothing can be
 * started — the two lines described the same work twice and neither of them
 * added up to the other.
 *
 * Splitting by where the work is, rather than counting it twice, is what makes
 * the number checkable: the board draws no backlog column, so "2 on the board
 * plus 1 in the backlog" is the only phrasing that matches what a reader can
 * actually see and count.
 */
export function stuckSummary(onBoard: number, inBacklog: number): string {
  const are = (n: number) => (n === 1 ? 'thing is' : 'things are');
  if (inBacklog === 0) return `${onBoard} ${are(onBoard)} holding this up`;
  if (onBoard === 0) {
    return `${inBacklog} ${are(inBacklog)} holding this up, ${inBacklog === 1 ? 'and it is' : 'all of them'} in the backlog`;
  }
  return `${onBoard} ${are(onBoard)} holding this up, plus ${inBacklog} in the backlog`;
}

/**
 * Best guess at the kind of blocker from free text. Used to fill the field on
 * import, and to pre-select the dropdown so a person is correcting rather than
 * starting from nothing.
 */
export function detectBlockerType(text: string): BlockerType | undefined {
  if (!text?.trim()) return undefined;
  if (/#\d/.test(text)) return 'item';
  return PATTERNS.find((p) => p.test.test(text))?.type;
}

const isClosed = (i?: Issue) => !i || i.status === 'done' || i.status === 'wontdo';

/** Ranked list of actions, the one releasing the most work first. */
export function unblockActions(issues: Issue[]): UnblockAction[] {
  const byRef = new Map(issues.map((i) => [i.ref, i]));
  const resolve = (ref: string) => byRef.get(ref) ?? byRef.get(ref.replace(/^(#\d+).*$/, '$1'));

  const open = issues.filter((i) => !isClosed(i));
  const groups = new Map<string, UnblockAction>();

  const add = (
    key: string,
    type: BlockerType,
    label: string,
    detail: string,
    exact: boolean,
    issue: Issue,
    blocker?: Issue
  ) => {
    const g = groups.get(key) ?? { key, type, label, detail, exact, issues: [], blocker };
    g.issues.push(issue);
    groups.set(key, g);
  };

  for (const issue of open) {
    /*
     * `stillBlocked`, not `blocked`. This grouping already ignored a dependency
     * on a FINISHED issue when working out what a bottleneck releases — but the
     * issue itself was still counted as blocked, so it appeared in the totals
     * and stayed out of "what next" while nothing was actually holding it.
     * The same rule everywhere, from one place. See src/blocked.ts.
     */
    /* stillBlocked already answers "nothing typed" and "a word that means
       nothing". A second raw check beside it is the shape the fault took. */
    if (!stillBlocked(issue, open)) continue;
    /* The reason, once, through the one function that knows a word meaning
       nothing is not a reason. Everything below reads `why`, not the field. */
    const why = blockerOf(issue);
    /*
     * '' means nobody has classified it, so fall back to reading the wording —
     * and so does a value this build has never heard of.
     *
     * A KIND THAT IS NOT ONE OF OURS IS NOT A KIND. `blockerType` is free text
     * in the database: an import, a tracker push, or an older release can put a
     * word in it that BLOCKER_ACTION has no entry for. Trusted blindly, the
     * lookup returned undefined and the panel drew a blocker with a heading
     * that was simply blank — a row saying nothing, above a list of the cards
     * it is holding up. Seen on a test copy, with `approval` in the field.
     */
    const known = issue.blockerType && BLOCKER_TYPES.includes(issue.blockerType) ? issue.blockerType : '';
    const type = known || detectBlockerType(why);
    const target = issue.blockerTarget?.trim();

    // Named target: everything waiting on the SAME thing groups together, and
    // clearing it really does release all of them. This is the difference
    // between "six items are waiting on a test" — which may be six different
    // tests, and cannot be acted on — and "run the cellular load test".
    if (target && type && type !== 'item') {
      const key = `${type}:${target.toLowerCase()}`;
      add(key, type, namedAction(type, target), DETAIL[type], true, issue);
      continue;
    }

    // A dependency on another open item is the one blocker with a named target,
    // so it groups per target and is exact — finishing it really does free them.
    if (type === 'item' || type === undefined) {
      // A named item wins over anything the wording happens to mention.
      const named = target ? [target] : (why.match(/#\d+[a-zA-Z-]*/g) ?? []);
      const onBoard = [...new Set(named)]
        .map(resolve)
        .filter((t): t is Issue => !!t && t.ref !== issue.ref);
      const deps = onBoard.filter((t) => !isClosed(t));

      if (deps.length > 0) {
        for (const d of deps) {
          add(`dep:${d.ref}`, 'item', `Finish ${d.ref} first`, d.title.slice(0, 90), true, issue, d);
        }
        continue;
      }
      // Marked as waiting on an item but naming none that is still open. If it
      // named something, say what — losing the reference to a generic "waiting
      // on something else" throws away the one useful fact it had.
      if (type === 'item') {
        // THE CARD IT NAMED IS FINISHED. This is the case worth catching: the
        // work happened, nobody came back to clear the flag, and these are
        // sitting still for no reason. It used to lose the card here — the
        // reference survived as text, but the issue behind it did not — so the
        // check before clearing had nothing to read in the one situation where
        // the answer is plainly yes. Carried now, so it can say so.
        const done = onBoard[0];
        if (done) {
          add(`named:${done.ref.toLowerCase()}`, 'item', `Waiting on ${done.ref}`,
            `${done.ref} is finished — nothing came back to clear this.`, true, issue, done);
        } else if (target) {
          add(`named:${target.toLowerCase()}`, 'item', `Waiting on ${target}`,
            'Named as the blocker, but no open item on this board matches it.', true, issue);
        } else {
          add('other', 'other', BLOCKER_ACTION.other, DETAIL.other, false, issue);
        }
        continue;
      }
    }

    // Nobody named the specific thing, so this is a heap of the same KIND of
    // wait rather than one job — which is the only case where the warning
    // about them possibly being different things belongs.
    const t: BlockerType = type ?? 'other';
    add(t, t, BLOCKER_ACTION[t], [DETAIL[t], UNNAMED[t]].filter(Boolean).join(' '), false, issue);
  }

  return [...groups.values()].sort(
    (a, b) => b.issues.length - a.issues.length || a.label.localeCompare(b.label)
  );
}

export { BLOCKER_LABEL };

/**
 * Whether clearing a group's blocker frees everything in it, in words.
 *
 * Returns null for a single item, because the distinction is meaningless there:
 * one item can only be waiting on one thing. Printing it anyway produced
 * "1 separate waits", which is both wrong and absurd.
 *
 * This was a pale orange rail on the side of a card before. Nobody decodes
 * "these may be waiting on different things" from two shades of the same
 * colour, so it is a sentence now — and a function, so it can be tested without
 * depending on whichever groups a particular board happens to contain.
 *
 * It carried the count too — "clears all 3", "3 separate waits" — which put the
 * same number twice in one header, beside "3 items". And nobody writes "3
 * separate waits". It says only what it is for now: whether one action does it.
 */
export function groupReach(exact: boolean, count: number): string | null {
  if (count <= 1) return null;
  return exact ? 'one action clears these' : 'these need separate actions';
}

/**
 * The one line that says what is actually holding an item up.
 *
 * Shown on the card and at the top of the issue, because a blocker recorded and
 * never displayed is worse than one never recorded: the person filling it in
 * believes the board now knows, while every reader still has to open the item
 * and read a paragraph to find out who is being waited on.
 */
export function blockerSummary(issue: {
  blocked?: string;
  blockerType?: BlockerType | '';
  blockerTarget?: string;
}): string | null {
  const why = blockerOf(issue);
  if (!why) return null;
  const target = issue.blockerTarget?.trim();
  const type = issue.blockerType || detectBlockerType(why);
  if (type && target) return namedAction(type, target);
  if (type) return BLOCKER_ACTION[type];
  return null;
}
